/* RoomServiceMenuItemsData.tsx */
import React, { useState, useEffect } from "react";
import {
  ChevronDownIcon,
  PencilIcon,
  XMarkIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { Dropdown } from "../../ui/dropdown/Dropdown";
import { DropdownItem } from "../../ui/dropdown/DropdownItem";
// Assuming MenuCategory can be reused or a new RoomServiceMenuCategory will be made
import MenuCategory from "./subcomponents/RestuarantComponents/MenuItemComponents/MenuCategory";
import SearchBar from "../../ui/SearchBar";
import { LoadingSpinner } from "../../ui/LoadingSpinner";
import { PageLoadingView } from "../../common/PageLoadingView";
import { PageErrorView } from "../../common/PageErrorView";
import ConfirmationDialog from "../../common/ConfirmationDialog";
// New hook for room service items and menus
import { useRoomServiceItems } from "../../../hooks/useRoomServiceItems";
import { DataManagementSidebar } from "./subcomponents/DataManagementSidebar";
import BulkAddRoomServiceItemsModal from "./subcomponents/BulkAddRoomServiceItemsModal";
// New type for room service items
import { RoomServiceItem } from "../../../types/roomServiceItem";
import { useUser } from "../../../context/UserInfoProvider";
// New hook for room service menu schedules
import { useRoomServiceMenuSchedules } from "../../../hooks/useRoomServiceMenuSchedules";
import { DayOfWeek } from "../../../types/restaurant"; // Assuming DayOfWeek can be reused
import { RoomServiceMenu } from "../../../types/roomServiceMenu";
import { RoomServiceMenuSchedule } from "../../../types/roomServiceMenuSchedule";
import { parseMenuItemError } from "../../../utils/menuItemErrorHandler";
import { useAuthenticatedApi } from "../../../hooks/useAuthenticatedApi";

// Define allDays and formatTimeForDisplay locally or import if they are sharable utils
const allDays: DayOfWeek[] = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// Helper to format time from HH:MM:SS or HH:MM to HH:MM for input fields
const formatTimeForInput = (timeString: string | null | undefined): string => {
  if (!timeString) return "";
  const parts = timeString.split(":");
  if (parts.length >= 2) {
    return `${parts[0]}:${parts[1]}`;
  }
  return "";
};

const formatTimeForDisplay = (timeString: string | null): string => {
  if (!timeString) return "No Data Available";
  const [hours, minutes] = timeString.split(":");
  const hour24 = parseInt(hours, 10);
  const minute = parseInt(minutes, 10);

  // Convert 24-hour to 12-hour format without timezone conversion
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const ampm = hour24 >= 12 ? "PM" : "AM";

  return `${hour12}:${minute.toString().padStart(2, "0")} ${ampm}`;
};

// Define props for the component
interface RoomServiceMenuItemsDataProps {
  initialMenuId?: string;
  initialItemId?: string;
}

const RoomServiceDataContainer: React.FC<{
  hotelId: string;
  initialMenuId?: string;
  initialItemId?: string;
}> = ({ hotelId, initialMenuId, initialItemId }) => {
  // All hooks and logic are now safely inside this component
  const { callApi } = useAuthenticatedApi();
  const [isRoomServiceMenuDropdownOpen, setIsRoomServiceMenuDropdownOpen] =
    useState(false);
  const [isAvailabilityDropdownOpen, setIsAvailabilityDropdownOpen] =
    useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [initializationError, setInitializationError] = useState<string | null>(
    null
  );

  const [categories, setCategories] = useState<string[]>([]);
  const [tempCategoryName, setTempCategoryName] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(
    null
  );

  // Availability controls removed; room service visibility managed elsewhere

  // Use the room service items hook
  const {
    items: roomServiceItems,
    roomServiceMenus,
    selectedRoomServiceMenuId,
    setSelectedRoomServiceMenuId,
    isLoading,
    error,
    updateItem: updateRoomServiceItem,
    deleteItem: deleteRoomServiceItem,
    createItem: createRoomServiceItem,
    selectedItem,
    setSelectedItem,
    fetchItems: refetchRoomServiceItems,
  } = useRoomServiceItems({
    hotelId,
    initialPage: 1,
    initialLimit: 10,
  });

  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Availability update state removed
  const [isDeletingItem, setIsDeletingItem] = useState<{
    [key: string]: boolean;
  }>({});
  const [isEditingItem] = useState<{
    [key: string]: boolean;
  }>({});
  const [isUpdatingCategoryItems, setIsUpdatingCategoryItems] = useState(false);
  
  // Bulk add state
  const [isBulkAddingItems, setIsBulkAddingItems] = useState(false);
  const [bulkAddError, setBulkAddError] = useState<string | null>(null);
  const [bulkAddRetainedRows, setBulkAddRetainedRows] = useState<
    Array<{
      itemname: string;
      category: string;
      description: string;
      ingredients: string;
      priceAmount: number;
      currencyCode: string;
      isavailable: boolean;
    }> | null
  >(null);

  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    cancelText?: string;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    confirmText: "Confirm",
    cancelText: "Cancel",
  });

  const [isScheduleSectionOpen, setIsScheduleSectionOpen] = useState(false);

  const roomSchedulesHook = useRoomServiceMenuSchedules({
    hotelId,
    roomServiceMenuId: selectedRoomServiceMenuId || "",
  });

  const roomServiceMenuSchedules = roomSchedulesHook.schedules;
  const isLoadingMenuSchedules = roomSchedulesHook.isLoading;
  const menuSchedulesError = roomSchedulesHook.error;
  const upsertRoomServiceMenuSchedule = roomSchedulesHook.upsertMenuSchedule;
  const deleteRoomServiceMenuScheduleForDay =
    roomSchedulesHook.deleteRoomServiceMenuScheduleForDay;

  // State for editing menu schedules
  const [editingScheduleDay, setEditingScheduleDay] =
    useState<DayOfWeek | null>(null);
  const [tempScheduleTimes, setTempScheduleTimes] = useState<{
    open: string;
    close: string;
  }>({ open: "", close: "" });
  const [isSavingSchedule, setIsSavingSchedule] = useState<DayOfWeek | null>(
    null
  );
  const [scheduleUpdateError, setScheduleUpdateError] = useState<string | null>(
    null
  );

  // Reinstated: Handle room service menu selection from dropdown
  const handleRoomServiceMenuSelect = (roomServiceMenuId: string) => {
    if (selectedRoomServiceMenuId !== roomServiceMenuId) {
      setSelectedRoomServiceMenuId(roomServiceMenuId);
      setSelectedItem(null); // Clear selected item when menu changes manually
      setCategories([]); // Reset categories as items will change
      // Optionally, clear search query or other related filters
    }
    setInitializationError(null); // Clear any init error if user manually selects a menu
    setIsRoomServiceMenuDropdownOpen(false); // Close dropdown after selection
  };

  // Initialize room service menu selection, prioritizing initialMenuId from props
  useEffect(() => {
    if (initialMenuId && roomServiceMenus.length > 0) {
      const menuExists = roomServiceMenus.some(
        (menu: RoomServiceMenu) => menu.roomservicemenuid === initialMenuId
      );
      if (menuExists) {
        if (initialMenuId !== selectedRoomServiceMenuId) {
          setSelectedRoomServiceMenuId(initialMenuId);
        }
        setInitializationError(null);
        return; // Exit if initialMenuId is processed and valid
      } else {
        console.warn(
          `Provided initialMenuId ${initialMenuId} not found in available menus. Falling back to default selection or error.`
        );
        // Fall through to default selection logic if initialMenuId is invalid
      }
    }

    // Default selection logic if initialMenuId is not provided or was invalid
    if (roomServiceMenus.length > 0 && !selectedRoomServiceMenuId) {
      const firstMenu = roomServiceMenus[0];
      setSelectedRoomServiceMenuId(firstMenu.roomservicemenuid);
      setInitializationError(null);
    } else if (
      roomServiceMenus.length === 0 &&
      !isLoading &&
      !selectedRoomServiceMenuId // Only show error if no menu is selected after attempting initial/default
    ) {
      setInitializationError(
        "No room service menus found for this hotel, or specified menu is unavailable."
      );
    }
  }, [
    initialMenuId,
    roomServiceMenus,
    selectedRoomServiceMenuId,
    setSelectedRoomServiceMenuId,
    isLoading,
  ]);

  // Effect to select item from URL prop, runs when initialItemId or items change, or menu selection is confirmed
  useEffect(() => {
    if (
      initialItemId &&
      selectedRoomServiceMenuId && // Ensure a menu is actually selected
      roomServiceItems && // Ensure items are loaded
      roomServiceItems.length > 0
    ) {
      // Filter items for the currently selected menu before finding the item
      const itemsInSelectedMenu = roomServiceItems.filter(
        (i: RoomServiceItem) =>
          i.roomservicemenuid === selectedRoomServiceMenuId
      );

      const itemToSelect = itemsInSelectedMenu.find(
        (i: RoomServiceItem) => i.rsitemid === initialItemId
      );

      if (itemToSelect) {
        setSelectedItem(itemToSelect); // This opens the item in the DataManagementSidebar
        console.log(
          `Room service item selected from URL prop: ${initialItemId} in menu ${selectedRoomServiceMenuId}`
        );
        // Optional: Implement scrolling to the item in the main list here
        // This would require refs and potentially expanding the category.
      } else {
        console.warn(
          `initialItemId ${initialItemId} not found in currently selected menu ${selectedRoomServiceMenuId}.`
        );
      }
    }
  }, [
    initialItemId,
    roomServiceItems,
    selectedRoomServiceMenuId, // Important dependency
    setSelectedItem,
  ]);

  useEffect(() => {
    if (roomServiceItems && Array.isArray(roomServiceItems)) {
      const uniqueCategories = Array.from(
        // Assuming 'category' field exists on RoomServiceItem
        new Set(
          roomServiceItems
            .map((item: RoomServiceItem) => item.category)
            .filter(Boolean)
        )
      );
      setCategories(uniqueCategories as string[]);
    } else {
      setCategories([]);
    }
  }, [roomServiceItems]);

  const handleRemoveCategory = async (catId: string) => {
    setConfirmationDialog({
      isOpen: true,
      title: "Delete Category",
      message: `Are you sure you want to delete the category "${catId}"? This will delete all room service items within this category and cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));

        setUpdateError(null);
        try {
          const itemsToDelete = roomServiceItems.filter(
            (item: RoomServiceItem) => item.category === catId
          );
          for (const item of itemsToDelete) {
            // Assuming rsitemid is the ID for RoomServiceItem
            await deleteRoomServiceItem(item.rsitemid);
          }
          setCategories((prev) => prev.filter((cat) => cat !== catId));
        } catch (error) {
          console.error("Failed to delete items or category:", error);
          const parsedError = parseMenuItemError(error);
          setUpdateError(parsedError.userMessage);
          setFieldErrors(parsedError.fieldErrors);
        }
      },
    });
  };

  const handleStartEdit = (catId: string) => {
    setEditingCategoryId(catId);
    setTempCategoryName(catId);
  };

  const handleSaveCategoryName = async () => {
    const trimmedName = tempCategoryName.trim();
    const oldCategoryName = editingCategoryId;

    if (!trimmedName) {
      setUpdateError("Category name cannot be empty.");
      return;
    }
    if (editingCategoryId === "new" && categories.includes(trimmedName)) {
      setUpdateError("Category name already exists.");
      return;
    }
    if (
      editingCategoryId !== "new" &&
      categories.some((cat) => cat === trimmedName && cat !== editingCategoryId)
    ) {
      setUpdateError("Category name already exists.");
      return;
    }

    setUpdateError(null);

    if (editingCategoryId === "new") {
      setCategories((prev) => [trimmedName, ...prev]);
    } else if (oldCategoryName && oldCategoryName !== trimmedName) {
      setCategories((prev) =>
        prev.map((cat) => (cat === oldCategoryName ? trimmedName : cat))
      );
      const itemsToUpdate = roomServiceItems.filter(
        (item: RoomServiceItem) => item.category === oldCategoryName
      );
      setIsUpdatingCategoryItems(true);
      try {
        for (const item of itemsToUpdate) {
          // Assuming rsitemid for RoomServiceItem
          await updateRoomServiceItem(item.rsitemid, { category: trimmedName });
        }
      } catch (error) {
        console.error("Failed to update items in renamed category:", error);
        const parsedError = parseMenuItemError(error);
        setUpdateError(parsedError.userMessage);
        setFieldErrors(parsedError.fieldErrors);
      } finally {
        setIsUpdatingCategoryItems(false);
      }
    }

    setEditingCategoryId(null);
    setTempCategoryName("");
  };

  const handleCancelCategoryNameEdit = () => {
    setEditingCategoryId(null);
    setTempCategoryName("");
  };

  const handleAddItem = (categoryId: string) => {
    // Clear any previous errors before opening new item sidebar
    setUpdateError(null);

    // Adapt for RoomServiceItem properties
    setSelectedItem({
      rsitemid: "", // Changed from menuitemid
      hotelid: hotelId,
      roomservicemenuid: selectedRoomServiceMenuId, // Changed
      photo: null,
      description: "",
      ingredients: "",
      category: categoryId,
      itemname: "",
      price: "", // This will be an object or UUID string depending on backend
      priceAmount: 0,
      currencyCode: "USD",
      isavailable: true,
      createdat: new Date().toISOString(),
      updatedat: new Date().toISOString(),
      // Add any other RoomServiceItem specific fields
    } as RoomServiceItem); // Cast to new type
    setIsCreating(true);
  };

  // Availability change handler removed

  const handleDeleteItem = async (itemId: string) => {
    // Find item to display name in confirmation
    const target = roomServiceItems.find((i) => i.rsitemid === itemId);
    const itemName = target?.itemname || "this item";

    setConfirmationDialog({
      isOpen: true,
      title: "Delete Room Service Item",
      message: `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));

        try {
          setIsDeletingItem((prev) => ({ ...prev, [itemId]: true }));
          setUpdateError(null);
          await deleteRoomServiceItem(itemId);
          // No need to refetch - the hook now updates state immediately
        } catch (error: any) {
          console.error("Error deleting room service item:", error);

          // Check if this is an FK reference error from the backend
          const errorMessage =
            error?.response?.data?.error?.message || error?.message || "";

          if (
            errorMessage.includes("Cannot delete room service item") &&
            errorMessage.includes("referenced in") &&
            errorMessage.includes("existing dining order")
          ) {
            const match = errorMessage.match(
              /referenced in (\d+) existing dining order/
            );
            const orderCount = match ? parseInt(match[1], 10) : 0;

            // Show second confirmation dialog for FK reference error
            setConfirmationDialog({
              isOpen: true,
              title: "Cannot Delete Item",
              message: `Cannot delete "${itemName}". It is referenced in ${orderCount} existing dining order(s).\n\nWould you like to set the availability to "Unavailable" instead? This will hide the item from guests.`,
              confirmText: "Set to Unavailable",
              cancelText: "Cancel",
              onConfirm: async () => {
                setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));
                if (target) {
                  try {
                    await updateRoomServiceItem(itemId, { isavailable: false });
                    await refetchRoomServiceItems();
                    setUpdateError(null);
                  } catch (availError) {
                    const parsedError = parseMenuItemError(availError);
                    setUpdateError(parsedError.userMessage);
                    setFieldErrors(parsedError.fieldErrors);
                    console.error("Error updating availability:", availError);
                  }
                }
              },
            });
          } else {
            // Use centralized error handler for all other errors
            const parsedError = parseMenuItemError(error);
            setUpdateError(parsedError.userMessage);
            setFieldErrors(parsedError.fieldErrors);
          }
        } finally {
          setIsDeletingItem((prev) => ({ ...prev, [itemId]: false }));
        }
      },
    });
  };

  // Availability dropdown logic removed

  const searchLower = searchQuery.toLowerCase();
  const filteredCategories = categories.filter((cat) => {
    if (cat.toLowerCase().includes(searchLower)) return true;
    return roomServiceItems.some(
      (item) =>
        item.category === cat &&
        item.itemname.toLowerCase().includes(searchLower)
    );
  });

  const handleItemSelect = (item: RoomServiceItem) => {
    // Changed type
    setSelectedItem(item);
  };

  const handleAvailabilityChange = async (itemId: string, isavailable: boolean) => {
    try {
      setUpdateError(null);
      await updateRoomServiceItem(itemId, { isavailable });
      await refetchRoomServiceItems();
    } catch (error) {
      console.error("Error updating availability:", error);
      const parsedError = parseMenuItemError(error);
      setUpdateError(parsedError.userMessage);
      setFieldErrors(parsedError.fieldErrors);
    }
  };

  // Bulk add room service items handler
  const handleSaveBulkRoomServiceItems = async (
    itemsData: Array<{
      itemname: string;
      category: string;
      description: string;
      ingredients: string;
      priceAmount: number;
      currencyCode: string;
      isavailable: boolean;
    }>
  ) => {
    if (!selectedRoomServiceMenuId) {
      throw new Error("No room service menu selected");
    }

    try {
      setIsUpdating(true);

      const results: Array<{
        itemname: string;
        success: boolean;
        error?: string;
        errorMessage?: string;
      }> = [];

      // Process all items and collect results
      for (const item of itemsData) {
        try {
          // Create price for the item
          const priceResponse = await callApi({
            method: "POST",
            url: `/management/prices`,
            data: {
              amount: item.priceAmount.toString(),
              currencyCode: item.currencyCode || "USD",
              priceType: "Room Service Item",
              description: `Price for ${item.itemname}`,
            },
          });

          const createdPriceId = priceResponse.data?.priceId;
          if (!createdPriceId) {
            throw new Error("Failed to create price for room service item");
          }

          // Create the room service item
          await callApi({
            method: "POST",
            url: `/management/hotel/room-service-menus/${selectedRoomServiceMenuId}/items`,
            data: {
              itemname: item.itemname,
              description: item.description || null,
              ingredients: item.ingredients || null,
              category: item.category || null,
              photo: null,
              priceid: createdPriceId,
              isactive: item.isavailable !== undefined ? item.isavailable : true,
            },
          });

          results.push({
            itemname: item.itemname,
            success: true,
          });
        } catch (err: any) {
          console.error(
            `Error creating room service item ${item.itemname}:`,
            err
          );
          const parsedError = parseMenuItemError(err);
          results.push({
            itemname: item.itemname,
            success: false,
            error: parsedError.userMessage,
            errorMessage:
              err?.response?.data?.error?.message || parsedError.userMessage,
          });
        }
      }

      // Check if there were any errors
      const failedResults = results.filter((result) => !result.success);
      const successCount = results.filter((result) => result.success).length;
      const totalCount = results.length;

      // If there are any errors, don't close modal and show inline errors
      if (failedResults.length > 0) {
        // Build detailed error message
        let errorMsg = `${failedResults.length} of ${totalCount} items failed to create. `;
        if (successCount > 0) {
          errorMsg += `${successCount} items were created successfully. `;
        }
        errorMsg += "Please review the errors below:\n\n";

        // Add specific error for each failed row
        failedResults.forEach((result) => {
          const rowNum =
            itemsData.findIndex(
              (item) => item.itemname === result.itemname
            ) + 1;
          errorMsg += `Row ${rowNum} (${result.itemname}): ${result.errorMessage}\n`;
        });

        setBulkAddError(errorMsg);

        // Persist failed rows so modal can rehydrate inputs after reload
        const failedRows = itemsData.filter((item) =>
          failedResults.some((r) => r.itemname === item.itemname)
        );
        setBulkAddRetainedRows(failedRows);

        // Refresh room service items for successful creates
        if (successCount > 0) {
          await refetchRoomServiceItems();
          // Extract unique new categories from successful items
          const newCategories = new Set(categories);
          itemsData
            .filter((item) =>
              results.some(
                (r) =>
                  r.itemname === item.itemname && r.success && item.category
              )
            )
            .forEach((item) => {
              if (item.category && !newCategories.has(item.category)) {
                newCategories.add(item.category);
              }
            });
          setCategories(Array.from(newCategories));
        }

        return results; // Don't close modal, stay open for corrections
      }

      // If all successful, close modal and refresh
      await refetchRoomServiceItems();

      // Extract unique new categories from all items
      const newCategories = new Set(categories);
      itemsData.forEach((item) => {
        if (item.category && !newCategories.has(item.category)) {
          newCategories.add(item.category);
        }
      });
      setCategories(Array.from(newCategories));

      setIsBulkAddingItems(false);
      setBulkAddError(null);
      setBulkAddRetainedRows(null);

      return results;
    } catch (err) {
      console.error("Error in bulk room service item creation:", err);
      setBulkAddError(
        err instanceof Error ? err.message : "Failed to create items"
      );
      return [];
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSave = async () => {
    if (!selectedItem) return;

    try {
      setIsUpdating(true);
      setUpdateError(null);
      setFieldErrors({});

      // Frontend validation for required fields when creating
      const missing: Record<string, string> = {};
      if (!selectedItem.itemname || selectedItem.itemname.trim() === "") {
        missing.itemname = "Item name is required";
      }
      const priceEmpty =
        (selectedItem.priceAmount ?? 0) <= 0 &&
        (!selectedItem.price || String(selectedItem.price).trim() === "");
      if (priceEmpty) {
        missing.price = "Price is required";
      }
      if (Object.keys(missing).length > 0) {
        setFieldErrors(missing);
        setUpdateError("Please fix the highlighted fields.");
        setIsUpdating(false);
        return;
      }

      if (isCreating) {
        // Construct the object with only the properties needed for creation.
        // 'price' is included as it was identified as required.
        const itemDataForCreation = {
          itemname: selectedItem.itemname,
          description: selectedItem.description,
          category: selectedItem.category,
          photo: selectedItem.photo,
          ingredients: selectedItem.ingredients,
          isavailable: selectedItem.isavailable,
          priceAmount: selectedItem.priceAmount,
          currencyCode: selectedItem.currencyCode,
          price: selectedItem.price, // Assuming price is required for creation
          // hotelid, roomservicemenuid, rsitemid, createdat, updatedat, resolvedPrice are excluded
        };
        await createRoomServiceItem(itemDataForCreation);
        await refetchRoomServiceItems();
      } else {
        // Construct the object with only the properties needed for update.
        const itemDataForUpdate = {
          itemname: selectedItem.itemname,
          description: selectedItem.description,
          category: selectedItem.category,
          photo: selectedItem.photo,
          ingredients: selectedItem.ingredients,
          isavailable: selectedItem.isavailable,
          priceAmount: selectedItem.priceAmount,
          currencyCode: selectedItem.currencyCode,
          price: selectedItem.price, // Assuming price can be updated
          // hotelid, roomservicemenuid, createdat, updatedat, resolvedPrice are excluded
          // rsitemid is passed as a separate argument to updateRoomServiceItem
        };
        await updateRoomServiceItem(selectedItem.rsitemid, itemDataForUpdate);
        await refetchRoomServiceItems();
      }

      // Successful save - close sidebar
      setSelectedItem(null);
      setIsCreating(false);
      setIsUpdating(false);
    } catch (error: any) {
      // Parse error first to check if we got field-specific errors
      const parsedError = parseMenuItemError(error);
      
      // If we got a "too long" error but no specific field was identified,
      // check the selectedItem to find which field is likely too long
      const errorMessage = error?.response?.data?.error?.message || "";
      if (
        (errorMessage.includes("too long") || error?.code === "22001") &&
        Object.keys(parsedError.fieldErrors).length === 0 &&
        selectedItem
      ) {
        // Check field lengths against typical limits
        const fieldChecks = [
          { field: "itemname", value: selectedItem.itemname, maxLength: 255 },
          { field: "description", value: selectedItem.description, maxLength: 2000 },
          { field: "ingredients", value: selectedItem.ingredients, maxLength: 2000 },
          { field: "category", value: selectedItem.category, maxLength: 100 },
        ];
        
        // Find the longest field that exceeds typical limits
        for (const check of fieldChecks) {
          if (check.value && String(check.value).length > 200) {
            const fieldLabel = check.field === "itemname" ? "Item Name" : 
                              check.field === "description" ? "Description" :
                              check.field === "ingredients" ? "Ingredients" :
                              check.field === "category" ? "Category" : check.field;
            parsedError.fieldErrors[check.field] = `${fieldLabel} is too long. Please shorten it.`;
            parsedError.userMessage = `${fieldLabel} is too long. Please reduce the length.`;
            break;
          }
        }
      }
      
      // Now set the errors using the parsed (and potentially enhanced) error
      setUpdateError(parsedError.userMessage);
      setFieldErrors(parsedError.fieldErrors);
      
      setIsUpdating(false); // Ensure isUpdating is set to false on error too
    }
  };

  const handleStartEditSchedule = (day: DayOfWeek) => {
    // Find all active schedules for this day, then pick the most recent one
    const activeSchedulesForDay = roomServiceMenuSchedules.filter(
      (s: RoomServiceMenuSchedule) =>
        s.dayOfWeek === day &&
        s.isActive &&
        s.roomServiceMenuId === selectedRoomServiceMenuId
    );
    
    // If multiple schedules exist, prefer the one with the most recent updatedat
    // If no timestamps, prefer the LAST item (most recently saved)
    const activeScheduleForDay = activeSchedulesForDay.length > 0
      ? (() => {
          const sorted = [...activeSchedulesForDay].sort((a, b) => {
            const aUpdated = (a as any).updatedat || (a as any).updatedAt || "";
            const bUpdated = (b as any).updatedat || (b as any).updatedAt || "";
            
            if (aUpdated && bUpdated) {
              return bUpdated.localeCompare(aUpdated);
            }
            if (aUpdated && !bUpdated) return -1;
            if (!aUpdated && bUpdated) return 1;
            return 0;
          });
          
          const hasTimestamps = sorted.some((s) => {
            const updated = (s as any).updatedat || (s as any).updatedAt || "";
            return updated !== "";
          });
          
          return hasTimestamps ? sorted[0] : sorted[sorted.length - 1];
        })()
      : null;
    
    setEditingScheduleDay(day);
    setTempScheduleTimes({
      open: activeScheduleForDay
        ? formatTimeForInput(activeScheduleForDay.startTime)
        : "",
      close: activeScheduleForDay
        ? formatTimeForInput(activeScheduleForDay.endTime)
        : "",
    });
    setScheduleUpdateError(null);
  };

  const handleCancelEditSchedule = () => {
    setEditingScheduleDay(null);
    setTempScheduleTimes({ open: "", close: "" });
    setScheduleUpdateError(null);
  };

  const handleSaveSchedule = async (day: DayOfWeek) => {
    if (!selectedRoomServiceMenuId) return;
    setIsSavingSchedule(day);
    setScheduleUpdateError(null);

    const { open, close } = tempScheduleTimes;
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;

    if ((open && !timeRegex.test(open)) || (close && !timeRegex.test(close))) {
      setScheduleUpdateError(
        "Invalid time format. Please use HH:MM (e.g., 09:00 or 17:30)."
      );
      setIsSavingSchedule(null);
      return;
    }
    if (open && close && open >= close) {
      setScheduleUpdateError("Open time must be before close time.");
      setIsSavingSchedule(null);
      return;
    }

    try {
      if (!open && !close) {
        const existingActiveSchedule = roomServiceMenuSchedules.find(
          (s: RoomServiceMenuSchedule) =>
            s.dayOfWeek === day &&
            s.isActive &&
            s.roomServiceMenuId === selectedRoomServiceMenuId
        );
        if (existingActiveSchedule) {
          // Use delete function for RoomServiceMenuSchedule
          await deleteRoomServiceMenuScheduleForDay(day);
        }
      } else if (open && close) {
        // Check if there's an existing schedule for this day that needs to be updated
        // Find all schedules for this day (there may be multiple)
        const schedulesForDay = roomServiceMenuSchedules.filter(
          (s: RoomServiceMenuSchedule) =>
            s.dayOfWeek === day &&
            s.roomServiceMenuId === selectedRoomServiceMenuId
        );
        
        // Prefer the most recently updated active schedule, or first active, or first overall
        const existingSchedule = schedulesForDay.find(s => s.isActive) || schedulesForDay[0];

        if (existingSchedule) {
          // Update existing schedule
          try {
            await upsertRoomServiceMenuSchedule(
              day,
              open,
              close,
              true,
              null,
              "MenuOperatingHours"
            );
          } catch (updateError) {
            console.error(`Failed to update schedule for ${day}:`, updateError);

            // If it's a conflict error, try deleting first then creating
            if (
              updateError instanceof Error &&
              updateError.message.includes("conflict")
            ) {
              try {
                await deleteRoomServiceMenuScheduleForDay(day);
                await new Promise((resolve) => setTimeout(resolve, 200));
                await upsertRoomServiceMenuSchedule(
                  day,
                  open,
                  close,
                  true,
                  null,
                  "MenuOperatingHours"
                );
              } catch (retryError) {
                console.error(
                  `Failed to retry schedule update for ${day}:`,
                  retryError
                );
                setScheduleUpdateError(
                  `Schedule update failed. Please try again.`
                );
                return;
              }
            } else {
              throw updateError;
            }
          }
        } else {
          // Create new schedule
          try {
            await upsertRoomServiceMenuSchedule(
              day,
              open,
              close,
              true,
              null,
              "MenuOperatingHours"
            );
          } catch (createError) {
            console.error(`Failed to create schedule for ${day}:`, createError);
            setScheduleUpdateError(
              `Failed to create schedule. Please try again.`
            );
            return;
          }
        }
      } else {
        setScheduleUpdateError(
          "Both open and close times are required to set an active schedule, or leave both blank for 'Closed'."
        );
        setIsSavingSchedule(null);
        return;
      }
      setEditingScheduleDay(null);
    } catch (error) {
      console.error(`Failed to save schedule for ${day}:`, error);
      setScheduleUpdateError(
        error instanceof Error ? error.message : "Failed to save schedule."
      );
    } finally {
      setIsSavingSchedule(null);
    }
  };

  if (initializationError) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-500">{initializationError}</p>
      </div>
    );
  }

  if (isLoading) {
    return <PageLoadingView />;
  }

  if (error) {
    return (
      <PageErrorView
        title="Error loading room service data"
        onRetry={refetchRoomServiceItems}
      />
    );
  }

  // Check if roomServiceMenus exist and has items
  if (roomServiceMenus.length === 0) {
    return <div>No room service menus found. Please create one first.</div>;
  }

  if (!selectedRoomServiceMenuId) {
    return <div>Please select a Room Service Menu.</div>;
  }

  const selectedRoomServiceMenu = roomServiceMenus.find(
    // Assuming roomservicemenuid is the ID field
    (menu: RoomServiceMenu) =>
      menu.roomservicemenuid === selectedRoomServiceMenuId
  );

  return (
    <div className="w-full p-6 font-sans text-gray-800 dark:text-white">
      <div className="flex items-center justify-between overflow-visible">
        <div className="flex items-center gap-2 overflow-visible">
          {/* Display selected Room Service Menu name */}
          <h1 className="text-2xl font-bold">
            {selectedRoomServiceMenu?.menuname || "Room Service"}
          </h1>
          <div className="relative">
            <button
              onClick={() =>
                setIsRoomServiceMenuDropdownOpen(!isRoomServiceMenuDropdownOpen)
              }
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
            >
              <ChevronDownIcon className="w-5 h-5 text-gray-800 dark:text-gray-400" />
            </button>
            <Dropdown
              isOpen={isRoomServiceMenuDropdownOpen}
              onClose={() => setIsRoomServiceMenuDropdownOpen(false)}
              className="min-w-[200px] z-[100]"
            >
              {roomServiceMenus.map((menu: RoomServiceMenu, index) => (
                <DropdownItem
                  // Assuming roomservicemenuid and menuname
                  key={menu.roomservicemenuid || `menu-${index}`}
                  onClick={() =>
                    handleRoomServiceMenuSelect(menu.roomservicemenuid)
                  }
                  className="whitespace-nowrap"
                >
                  {menu.menuname ||
                    `Menu ${menu.roomservicemenuid.substring(0, 4)}`}
                </DropdownItem>
              ))}
            </Dropdown>
          </div>
        </div>
        {/* Removed the old menu selection for restaurant menus */}
      </div>

      {/* Search and Add Category */}
      <div className="mt-8 flex items-center justify-between">
        <div className="w-full mr-4">
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            placeholder="Search room service items..." // Changed placeholder
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsBulkAddingItems(true)}
            className="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-500 whitespace-nowrap"
          >
            Bulk Add Items
          </button>
          <button
            onClick={() => {
              setEditingCategoryId("new");
              setTempCategoryName("");
            }}
            className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-500 whitespace-nowrap"
          >
            + Add Category
          </button>
        </div>
      </div>

      {/* Menu Operating Hours Section (for the selected Room Service Menu) */}
      {selectedRoomServiceMenuId && roomServiceMenus.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between w-full py-2">
            <span
              onClick={() => setIsScheduleSectionOpen(!isScheduleSectionOpen)}
              className="text-lg font-semibold text-gray-800 dark:text-white cursor-pointer"
            >
              {selectedRoomServiceMenu?.menuname || "Selected Menu"} Operating
              Hours
            </span>
            <button
              onClick={() => setIsScheduleSectionOpen(!isScheduleSectionOpen)}
              className="p-1.5 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full focus:outline-none"
            >
              <ChevronDownIcon
                className={`w-5 h-5 transition-transform ${
                  isScheduleSectionOpen ? "transform rotate-180" : ""
                }`}
              />
            </button>
          </div>
          {isScheduleSectionOpen && (
            <div className="mt-4 pl-2 space-y-3 border-l-2 border-gray-200 dark:border-gray-700">
              {isLoadingMenuSchedules ? (
                <div className="flex justify-center items-center h-24">
                  <LoadingSpinner />
                </div>
              ) : menuSchedulesError ? ( // This is from useRoomServiceMenuSchedules
                <div className="text-red-500 text-center">
                  Error loading menu schedules:{" "}
                  {typeof menuSchedulesError === "string"
                    ? menuSchedulesError
                    : menuSchedulesError?.message}
                </div>
              ) : (
                allDays.map((day: DayOfWeek) => {
                  // Find all active schedules for this day with the correct menu ID
                  const activeSchedulesForDay = roomServiceMenuSchedules.filter(
                    (s: RoomServiceMenuSchedule) =>
                      s.dayOfWeek === day &&
                      s.isActive &&
                      s.roomServiceMenuId === selectedRoomServiceMenuId
                  );
                  
                  // If multiple schedules exist, prefer the one with the most recent updatedat
                  // Sort by updatedat descending (most recent first), then select the first one
                  // If no timestamps are available, prefer the LAST item in the array (most recently saved)
                  const activeScheduleForDay = activeSchedulesForDay.length > 0
                    ? (() => {
                        const sorted = [...activeSchedulesForDay].sort((a, b) => {
                          const aUpdated = (a as any).updatedat || (a as any).updatedAt || "";
                          const bUpdated = (b as any).updatedat || (b as any).updatedAt || "";
                          
                          // If both have timestamps, compare them (newer is first)
                          if (aUpdated && bUpdated) {
                            return bUpdated.localeCompare(aUpdated);
                          }
                          // If only one has timestamp, prefer the one with timestamp
                          if (aUpdated && !bUpdated) return -1;
                          if (!aUpdated && bUpdated) return 1;
                          // If neither has timestamp, maintain original order
                          return 0;
                        });
                        
                        // Check if any schedules have timestamps
                        const hasTimestamps = sorted.some((s) => {
                          const updated = (s as any).updatedat || (s as any).updatedAt || "";
                          return updated !== "";
                        });
                        
                        // If no timestamps, prefer the LAST item (most recently saved)
                        // Otherwise, use the first item from sorted (most recent timestamp)
                        return hasTimestamps ? sorted[0] : sorted[sorted.length - 1];
                      })()
                    : null;

                  const isEditingThisDay = editingScheduleDay === day;

                  return (
                    <div
                      key={day}
                      className="flex flex-col sm:flex-row justify-between sm:items-center border-b border-gray-200 dark:border-gray-700 pb-3 last:border-b-0 pt-1"
                    >
                      <span className="w-full sm:w-28 text-gray-700 dark:text-gray-300 mb-2 sm:mb-0">
                        {day}
                      </span>
                      {isEditingThisDay ? (
                        <div className="flex items-center">
                          <div className="text-right mr-2 flex-grow flex items-center justify-end gap-2">
                            <input
                              type="time"
                              value={tempScheduleTimes.open}
                              onChange={(e) =>
                                setTempScheduleTimes((prev) => ({
                                  ...prev,
                                  open: e.target.value,
                                }))
                              }
                              className="form-input w-24 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm p-1"
                            />
                            <span className="text-gray-500 dark:text-gray-400">
                              -
                            </span>
                            <input
                              type="time"
                              value={tempScheduleTimes.close}
                              onChange={(e) =>
                                setTempScheduleTimes((prev) => ({
                                  ...prev,
                                  close: e.target.value,
                                }))
                              }
                              className="form-input w-24 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm p-1"
                            />
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleSaveSchedule(day)}
                              disabled={isSavingSchedule === day}
                              className="p-1.5 text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400 disabled:opacity-50"
                            >
                              {isSavingSchedule === day ? (
                                <LoadingSpinner size="sm" />
                              ) : (
                                <CheckIcon className="w-5 h-5" />
                              )}
                            </button>
                            <button
                              onClick={handleCancelEditSchedule}
                              className="p-1.5 text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400"
                            >
                              <XMarkIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <div className="text-right mr-2 flex-grow">
                            {activeScheduleForDay &&
                            activeScheduleForDay.startTime && // Ensure field names
                            activeScheduleForDay.endTime ? (
                              <span className="text-gray-800 dark:text-gray-300">
                                {formatTimeForDisplay(
                                  activeScheduleForDay.startTime
                                )}{" "}
                                -{" "}
                                {formatTimeForDisplay(
                                  activeScheduleForDay.endTime
                                )}
                              </span>
                            ) : (
                              <span className="text-gray-500 dark:text-gray-400">
                                No Data Available
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => handleStartEditSchedule(day)}
                            className="p-1.5 text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              {scheduleUpdateError && (
                <p className="text-red-500 text-sm mt-2 text-center">
                  {scheduleUpdateError}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="border-b border-gray-200 dark:border-gray-700 my-6"></div>

      {isUpdatingCategoryItems && (
        <div className="flex justify-center items-center my-4 p-4 bg-blue-50 dark:bg-gray-800 rounded-lg">
          <LoadingSpinner />
          <p className="ml-3 text-blue-700 dark:text-blue-300">
            Updating item categories...
          </p>
        </div>
      )}

      {/* Displaying Room Service Items by Category */}
      {isLoading ? ( // This isLoading is from useRoomServiceItems
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <PageErrorView
          title="Error loading items"
          onRetry={refetchRoomServiceItems}
        />
      ) : (
        <div className="space-y-6 mt-6">
          {editingCategoryId === "new" && (
            <div className="border rounded-lg p-4 bg-white dark:bg-gray-800 shadow-sm">
              <div className="flex items-center gap-4">
                <input
                  type="text"
                  className="flex-1 border rounded px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                  value={tempCategoryName}
                  onChange={(e) => setTempCategoryName(e.target.value)}
                  placeholder="Enter category name"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveCategoryName}
                    className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-500"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelCategoryNameEdit}
                    className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white py-2 px-4 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
              {updateError && ( // This is general updateError, can be from category or item save
                <p className="text-red-500 text-sm mt-2">{updateError}</p>
              )}
            </div>
          )}

          {/* Show message if no categories or all categories are empty */}
          {filteredCategories.length === 0 && !(editingCategoryId === "new") ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">
                  No items available
                </p>
                <p className="text-gray-400 dark:text-gray-500 text-sm">
                  Create a category and add an item to fill this menu
                </p>
              </div>
            </div>
          ) : (
            filteredCategories.map((cat) => {
              // Filter roomServiceItems for the current category.
              // The roomServiceItems array should already be scoped to the selectedRoomServiceMenuId by the hook.
              const itemsInCategory = roomServiceItems.filter(
                (item: RoomServiceItem) =>
                  (item.category || "Default") === cat &&
                  (searchLower === "" ||
                    item.itemname.toLowerCase().includes(searchLower) ||
                    cat.toLowerCase().includes(searchLower))
              );
              
              // Sort items by createdat ascending (oldest first, newest last)
              const sortedItems = [...itemsInCategory].sort((a, b) => {
                const aCreated = a.createdat || "";
                const bCreated = b.createdat || "";
                if (aCreated && bCreated) {
                  return aCreated.localeCompare(bCreated);
                }
                if (aCreated && !bCreated) return -1;
                if (!aCreated && bCreated) return 1;
                return 0;
              });
              
              return (
                // Assuming MenuCategory component can be adapted or a new RoomServiceMenuCategory is used
                <MenuCategory
                  key={cat}
                  category={{
                    id: cat,
                    name: cat,
                    // Adapt items for RoomServiceItem structure
                    items: sortedItems.map((item: RoomServiceItem) => ({
                      id: item.rsitemid,
                      name: item.itemname,
                      // Price structure needs to match RoomServiceItem
                      price: item.price || "", // Provide empty string if price is null
                      priceAmount: item.priceAmount || 0,
                      currencyCode: item.currencyCode || "USD",
                      photo: item.photo,
                      isavailable: item.isavailable,
                    })),
                  }}
                  isEditingName={editingCategoryId === cat}
                  tempName={tempCategoryName}
                  onEditName={setTempCategoryName}
                  onSaveName={handleSaveCategoryName}
                  onCancelEdit={handleCancelCategoryNameEdit}
                  onStartEdit={() => handleStartEdit(cat)}
                  onRemove={() => handleRemoveCategory(cat)}
                  onAddItem={() => handleAddItem(cat)}
                  onEditItem={(itemId) => {
                    const item = roomServiceItems.find(
                      (i: RoomServiceItem) => i.rsitemid === itemId
                    );
                    if (item) handleItemSelect(item as RoomServiceItem);
                  }}
                  onDeleteItem={(itemId) => handleDeleteItem(itemId)}
                  onAvailabilityChange={handleAvailabilityChange}
                  isDeletingItem={isDeletingItem}
                  isEditingItem={isEditingItem}
                />
              );
            })
          )}
        </div>
      )}

      {/* Sidebar for Adding/Editing Room Service Item */}
      <DataManagementSidebar
        title={isCreating ? "Add Room Service Item" : "Edit Room Service Item"}
        selectedItem={selectedItem as RoomServiceItem} // Cast
        onClose={() => {
          setSelectedItem(null);
          setIsCreating(false);
        }}
        onSave={handleSave}
        isUpdating={isUpdating}
        updateError={updateError} // General updateError
        allCategories={categories}
        onSelectedItemChange={(newItemState) =>
          setSelectedItem(newItemState as RoomServiceItem)
        } // Cast
      >
        {selectedItem && (
          <div className="space-y-4">
            {/* Show general error message at the top if hideFooterError is true */}
            {updateError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-red-700 dark:text-red-300 text-sm font-medium">
                  {updateError}
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Photo
              </label>
              {/* Image uploading is not supported for this hotel on this page. Keeping the original uploader UI commented for future hotels. */}
              <div className="p-4 bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-center mt-2">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Photo uploading is disabled for this hotel.
                </p>
              </div>
              {/**
               * Original uploader UI (disabled for this hotel):
               *
               * {isCreating ? (
               *   <div className="p-4 bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-center mt-2">
               *     <p className="text-sm text-gray-600 dark:text-gray-400">
               *       Please save the room service item first to upload a photo
               *     </p>
               *   </div>
               * ) : !selectedItem.rsitemid ? (
               *   <div className="p-4 bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-center mt-2">
               *     <p className="text-sm text-gray-600 dark:text-gray-400">
               *       Room service item ID is missing. Please refresh the page or select a different item.
               *     </p>
               *   </div>
               * ) : (
               *   <div className="space-y-4 mt-2">
               *     <ImageUploader
               *       entityType="roomserviceitem-photo"
               *       entityId={selectedItem.rsitemid}
               *       currentImageUrl={selectedItem.photo}
               *       onUploadComplete={handlePhotoUpload}
               *       onUploadError={(error) => {
               *         setUpdateError(error);
               *       }}
               *       onDeleteImage={() => {
               *         setSelectedItem({
               *           ...selectedItem,
               *           photo: null,
               *         } as RoomServiceItem);
               *       }}
               *       disabled={isUpdating}
               *     />
               *     {updateError && (
               *       <p className="text-red-500 text-sm mt-2">{updateError}</p>
               *     )}
               *   </div>
               * )}
               */}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Name
              </label>
              <input
                type="text"
                value={selectedItem.itemname}
                onChange={(e) =>
                  setSelectedItem({
                    ...selectedItem,
                    itemname: e.target.value,
                  } as RoomServiceItem)
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {fieldErrors.itemname && (
                <p className="text-red-500 text-sm mt-1">
                  {fieldErrors.itemname}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <textarea
                value={selectedItem.description || ""}
                onChange={(e) =>
                  setSelectedItem({
                    ...selectedItem,
                    description: e.target.value,
                  } as RoomServiceItem)
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Ingredients
              </label>
              <textarea
                value={selectedItem.ingredients || ""}
                onChange={(e) =>
                  setSelectedItem({
                    ...selectedItem,
                    ingredients: e.target.value,
                  } as RoomServiceItem)
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Price
              </label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                  {selectedItem.currencyCode || "USD"}
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={
                    isCreating && selectedItem.priceAmount === 0
                      ? ""
                      : selectedItem.priceAmount
                  }
                  onChange={(e) =>
                    setSelectedItem({
                      ...selectedItem,
                      priceAmount:
                        e.target.value === ""
                          ? 0
                          : parseFloat(e.target.value) || 0,
                    } as RoomServiceItem)
                  }
                  className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300"
                />
              </div>
              {fieldErrors.price && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.price}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Availability
              </label>
              <div className="relative mt-1 inline-block">
                <button
                  type="button"
                  onClick={() => setIsAvailabilityDropdownOpen(!isAvailabilityDropdownOpen)}
                  className="dropdown-toggle inline-flex items-center gap-2 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm px-4 py-2 text-sm focus:border-blue-500 focus:ring-blue-500 text-gray-800 dark:text-white whitespace-nowrap"
                >
                  {selectedItem.isavailable !== undefined && (
                    <span
                      className={`w-2.5 h-2.5 rounded-full ${
                        selectedItem.isavailable ? "bg-green-500" : "bg-red-500"
                      }`}
                    />
                  )}
                  <span>
                    {selectedItem.isavailable !== undefined
                      ? selectedItem.isavailable
                        ? "Available"
                        : "Unavailable"
                      : "Select availability"}
                  </span>
                  <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                </button>
                <Dropdown
                  isOpen={isAvailabilityDropdownOpen}
                  onClose={() => setIsAvailabilityDropdownOpen(false)}
                  className="min-w-[160px] z-[100]"
                >
                  <DropdownItem
                    onClick={() => {
                      setSelectedItem({
                        ...selectedItem,
                        isavailable: true,
                      } as RoomServiceItem);
                      setIsAvailabilityDropdownOpen(false);
                    }}
                    active={selectedItem.isavailable === true}
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                      Available
                    </div>
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => {
                      setSelectedItem({
                        ...selectedItem,
                        isavailable: false,
                      } as RoomServiceItem);
                      setIsAvailabilityDropdownOpen(false);
                    }}
                    active={selectedItem.isavailable === false}
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                      Unavailable
                    </div>
                  </DropdownItem>
                </Dropdown>
              </div>
              {fieldErrors.isavailable && (
                <p className="text-red-500 text-sm mt-1">
                  {fieldErrors.isavailable}
                </p>
              )}
            </div>
          </div>
        )}
      </DataManagementSidebar>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onConfirm={confirmationDialog.onConfirm}
        onCancel={() =>
          setConfirmationDialog((prev) => ({ ...prev, isOpen: false }))
        }
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText={confirmationDialog.confirmText}
        cancelText={confirmationDialog.cancelText}
      />

      {/* Bulk Add Room Service Items Modal */}
      {isBulkAddingItems && (
        <BulkAddRoomServiceItemsModal
          isOpen={true}
          onClose={() => {
            setIsBulkAddingItems(false);
            setBulkAddError(null);
            setBulkAddRetainedRows(null);
          }}
          existingCategories={categories}
          onSave={handleSaveBulkRoomServiceItems}
          isCreating={isUpdating}
          error={bulkAddError}
          retainedRows={bulkAddRetainedRows || undefined}
        />
      )}
    </div>
  );
};

const RoomServiceMenuItemsData: React.FC<RoomServiceMenuItemsDataProps> = ({
  initialMenuId,
  initialItemId,
}) => {
  const { hotelId } = useUser();

  if (!hotelId) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
        <p className="ml-3">Loading user data...</p>
      </div>
    );
  }

  return (
    <RoomServiceDataContainer
      hotelId={hotelId}
      initialMenuId={initialMenuId}
      initialItemId={initialItemId}
    />
  );
};

export default RoomServiceMenuItemsData;
